import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CMAComp {
  id: string;
  cma_id: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  price: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  lot_size: number | null;
  price_per_sqft: number | null;
  property_type: string | null;
  status: string | null;
  days_on_market: number | null;
  zillow_url: string | null;
  image_url: string | null;
  visible: boolean;
  created_at: string;
}

export interface CMA {
  id: string;
  user_id: string | null;
  team_id: string | null;
  seller_name: string | null;
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  zpid: string | null;
  zillow_url: string | null;
  property_data: any;
  suggested_price_low: number | null;
  suggested_price_high: number | null;
  agent_price_low: number | null;
  agent_price_high: number | null;
  ai_analysis: string | null;
  ai_analysis_structured: any;
  agent_notes: string | null;
  agent_intel: string | null;
  custom_agent_name: string | null;
  custom_agent_headshot_url: string | null;
  custom_agent_phone: string | null;
  custom_agent_email: string | null;
  custom_logo_url: string | null;
  custom_footer_logo_url: string | null;
  primary_brand_color: string;
  secondary_brand_color: string;
  is_published: boolean;
  published_at: string | null;
  converted_north_id: string | null;
  is_processing: boolean;
  public_url_views_count: number;
  price: number | null;
  custom_property_image_url: string | null;
  zip_sold_dom_median: number | null;
  // Neighborhood profile
  neighborhood_data: any | null;
  neighborhood_data_updated_at: string | null;
  // Seller feedback
  seller_feedback_sentiment: "too_high" | "looks_right" | "too_low" | null;
  seller_feedback_comment: string | null;
  seller_feedback_at: string | null;
  // Net sheet
  net_sheet_mortgage_payoff: number | null;
  net_sheet_commission_pct: number | null;
  net_sheet_closing_cost_pct: number | null;
  net_sheet_seller_credits: number | null;
  net_sheet_other_fees: number | null;
  created_at: string;
  updated_at: string;
}

export interface CMADocument {
  id: string;
  cma_id: string;
  team_id: string;
  file_name: string;
  file_description: string | null;
  storage_path: string | null;
  public_url: string | null;
  link_url: string | null;
  file_size_bytes: number | null;
  created_at: string;
}

export function useCMAQuery(cmaId: string | undefined) {
  return useQuery({
    queryKey: ["cma", cmaId],
    queryFn: async () => {
      if (!cmaId) throw new Error("No CMA ID");
      const { data, error } = await supabase
        .from("cmas")
        .select("*")
        .eq("id", cmaId)
        .single();
      if (error) throw error;
      return data as CMA;
    },
    enabled: !!cmaId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useCMACompsQuery(cmaId: string | undefined) {
  return useQuery({
    queryKey: ["cma-comps", cmaId],
    queryFn: async () => {
      if (!cmaId) throw new Error("No CMA ID");
      const { data, error } = await supabase
        .from("cma_comparable_properties")
        .select("*")
        .eq("cma_id", cmaId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as CMAComp[];
    },
    enabled: !!cmaId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useCMAListQuery(teamId: string | undefined) {
  return useQuery({
    queryKey: ["cma-list", teamId],
    queryFn: async () => {
      if (!teamId) throw new Error("No team ID");
      const { data, error } = await supabase
        .from("cmas")
        .select("*")
        .eq("team_id", teamId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as CMA[];
    },
    enabled: !!teamId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useCMAUpdateMutation(cmaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Partial<CMA>) => {
      const { data, error } = await supabase
        .from("cmas")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", cmaId)
        .select()
        .single();
      if (error) throw error;
      return data as CMA;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["cma", cmaId], data);
    },
  });
}

export function useCMACompsMutation(cmaId: string) {
  const queryClient = useQueryClient();

  const addComp = useMutation({
    mutationFn: async (comp: Omit<CMAComp, "id" | "cma_id" | "created_at">) => {
      const { data, error } = await supabase
        .from("cma_comparable_properties")
        .insert([{ ...comp, cma_id: cmaId }])
        .select()
        .single();
      if (error) throw error;
      return data as CMAComp;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cma-comps", cmaId] });
    },
  });

  const updateComp = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CMAComp> }) => {
      const { data, error } = await supabase
        .from("cma_comparable_properties")
        .update(updates)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as CMAComp;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cma-comps", cmaId] });
    },
  });

  const deleteComp = useMutation({
    mutationFn: async (compId: string) => {
      const { error } = await supabase
        .from("cma_comparable_properties")
        .delete()
        .eq("id", compId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cma-comps", cmaId] });
    },
  });

  return { addComp, updateComp, deleteComp };
}

export function useCMADocumentsQuery(cmaId: string | undefined) {
  return useQuery({
    queryKey: ["cma-documents", cmaId],
    queryFn: async () => {
      if (!cmaId) throw new Error("No CMA ID");
      const { data, error } = await supabase
        .from("cma_documents")
        .select("*")
        .eq("cma_id", cmaId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as CMADocument[];
    },
    enabled: !!cmaId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useAddCMADocumentMutation(cmaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (doc: Omit<CMADocument, "id" | "cma_id" | "created_at">) => {
      const { data, error } = await supabase
        .from("cma_documents")
        .insert([{ ...doc, cma_id: cmaId }])
        .select()
        .single();
      if (error) throw error;
      return data as CMADocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cma-documents", cmaId] });
    },
  });
}

export function useDeleteCMADocumentMutation(cmaId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (doc: CMADocument) => {
      // Remove from storage if it's a file upload
      if (doc.storage_path) {
        await supabase.storage.from("logos").remove([doc.storage_path]);
      }
      const { error } = await supabase
        .from("cma_documents")
        .delete()
        .eq("id", doc.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cma-documents", cmaId] });
    },
  });
}
